// src/utils/agentBranding.js
// Partner (travel agent) branding for documents they send to their customers, e.g. the
// itinerary PDF: company name, logo and contact instead of RouteChef's.
//
// Name, phone and email come from the partner profile they registered with. The logo is
// uploaded by the partner (partner dashboard > Profile, or the itinerary download sheet):
//   - resized in the browser to at most 600 x 240 px (PNG, or JPEG when the PNG is large)
//   - saved to the account with intent "agent_update_branding" { logo_data_url }  ("" removes it);
//     the backend keeps it in a private S3 bucket
//   - read back with intent "agent_branding" only when needed, and cached on this device
//     against profile.logo_updated_at, so it is fetched once per change, not on every page
// agent_profile only says whether there is a logo (has_logo, logo_updated_at).
import { useEffect, useState } from "react";
import { API_URL, getAgentFromField, getAgentAuthHeaders, getAgentSession, verifyAgentSession, useAgentSession } from "./agentAuth";

const LOGO_EVENT = "rc-agent-logo-change";
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_W = 600;
const MAX_H = 240;
const MAX_DATA_URL = 250_000; // chars, ~180 KB of image: small enough for a DynamoDB item

// Device cache: { v: logo_updated_at ("" = saved on this device only), data: data URL }
const localKey = (email) => `rc_agent_logo_${String(email || "").toLowerCase()}`;
const readCache = (email) => {
    try { const c = JSON.parse(localStorage.getItem(localKey(email)) || "null"); return c && typeof c.data === "string" ? c : null; }
    catch { return null; }
};
const writeCache = (email, data, v = "") => {
    try {
        if (data) localStorage.setItem(localKey(email), JSON.stringify({ v, data }));
        else localStorage.removeItem(localKey(email));
    } catch { /* storage blocked */ }
    window.dispatchEvent(new Event(LOGO_EVENT));
};
// Backends that know about logos send has_logo; older ones don't, and then the device copy is all there is
const serverKnowsLogos = (p) => p && Object.prototype.hasOwnProperty.call(p, "has_logo");

async function postAgent(intent, payload = {}) {
    const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ From: getAgentFromField(), intent, headers: getAgentAuthHeaders(), ...payload }),
    });
    const json = await res.json().catch(() => ({}));
    return { status: json?.status ?? res.status, data: json?.data ?? null, message: json?.message || "" };
}

/** Make sure this device has the account's current logo (one agent_branding call per change). */
let syncing = null;
export function syncAgentLogo(session = getAgentSession()) {
    const p = session?.profile;
    if (!p || !serverKnowsLogos(p)) return Promise.resolve();
    const cached = readCache(p.email);
    if (!p.has_logo) {
        if (cached && cached.v) writeCache(p.email, "");          // removed on another device
        return Promise.resolve();
    }
    if (cached && cached.v === p.logo_updated_at) return Promise.resolve();
    if (syncing) return syncing;
    syncing = postAgent("agent_branding")
        .then((r) => { if (r.status === 200 && r.data?.logo_data_url) writeCache(p.email, r.data.logo_data_url, r.data.logo_updated_at || p.logo_updated_at); })
        .catch(() => { /* offline: keep what we have */ })
        .finally(() => { syncing = null; });
    return syncing;
}

/** Branding for the signed-in partner, or null when no partner is signed in. */
export function getAgentBranding(session = getAgentSession()) {
    if (!session?.profile) return null;
    const p = session.profile;
    return {
        partner: true,
        name: p.business_name || p.name || "",
        phone: p.phone || "",
        email: p.email || "",
        logo: readCache(p.email)?.data || "",
    };
}

/** React hook: partner branding, updated on sign in/out and when the logo changes. */
export function useAgentBranding() {
    const session = useAgentSession();
    const [, bump] = useState(0);
    useEffect(() => {
        const on = () => bump((n) => n + 1);
        window.addEventListener(LOGO_EVENT, on);
        return () => window.removeEventListener(LOGO_EVENT, on);
    }, []);
    useEffect(() => { syncAgentLogo(session); }, [session?.profile?.email, session?.profile?.logo_updated_at, session?.profile?.has_logo]); // eslint-disable-line react-hooks/exhaustive-deps
    return getAgentBranding(session);
}

/** Image file -> data URL sized for documents. Rejects non-images and files over 5 MB. */
export function resizeLogo(file) {
    return new Promise((resolve, reject) => {
        if (!file || !/^image\/(png|jpe?g|webp|svg\+xml|gif)$/i.test(file.type)) {
            reject(new Error("Please choose a PNG, JPG, WEBP or SVG image."));
            return;
        }
        if (file.size > MAX_FILE_BYTES) { reject(new Error("That image is over 5 MB. Please choose a smaller file.")); return; }
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            try {
                const w0 = img.naturalWidth || img.width || MAX_W;
                const h0 = img.naturalHeight || img.height || MAX_H;
                const s = Math.min(1, MAX_W / w0, MAX_H / h0);
                const w = Math.max(1, Math.round(w0 * s));
                const h = Math.max(1, Math.round(h0 * s));
                const c = document.createElement("canvas");
                c.width = w; c.height = h;
                const ctx = c.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                let out = c.toDataURL("image/png");
                if (out.length > MAX_DATA_URL) {
                    // Large PNG: JPEG on white keeps it small (transparency becomes white)
                    const j = document.createElement("canvas");
                    j.width = w; j.height = h;
                    const jx = j.getContext("2d");
                    jx.fillStyle = "#fff"; jx.fillRect(0, 0, w, h); jx.drawImage(c, 0, 0);
                    out = j.toDataURL("image/jpeg", 0.85);
                }
                if (out.length > MAX_DATA_URL) throw new Error("This logo is too detailed to save. Please try a simpler or smaller image.");
                resolve(out);
            } catch (e) {
                reject(e instanceof Error ? e : new Error("Couldn't read that image."));
            } finally {
                URL.revokeObjectURL(url);
            }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Couldn't read that image.")); };
        img.src = url;
    });
}

/**
 * Save (or remove, with "") the partner's logo on their account, and on this device.
 * Returns { ok, where: "account" | "device", message }.
 */
export async function saveAgentLogo(dataUrl) {
    const s = getAgentSession();
    if (!s) return { ok: false, where: "", message: "Please sign in as a partner first." };
    try {
        const r = await postAgent("agent_update_branding", { logo_data_url: dataUrl || "" });
        if (r.status === 200) {
            writeCache(s.profile.email, dataUrl || "", r.data?.profile?.logo_updated_at || "");
            await verifyAgentSession({ force: true });   // profile now carries has_logo / logo_updated_at
            return { ok: true, where: "account", message: dataUrl ? "Logo saved to your account." : "Logo removed." };
        }
        if (r.status === 400) return { ok: false, where: "", message: r.message || "That image couldn't be used." };
        if (r.status === 401 || r.status === 403) return { ok: false, where: "", message: "Your partner session has expired. Please sign in again." };
    } catch { /* offline: keep it on this device */ }
    writeCache(s.profile.email, dataUrl || "");
    return { ok: true, where: "device", message: dataUrl ? "Saved on this device only (couldn't reach the server). Upload it again later to save it to your account." : "Logo removed." };
}
